<template>
  <div
    class="d-inline-block"
  >
    <base-dialog-fullscreen
      :value="show"
      @input="onFullscreenInput"
      :title="title"
    >
      <template v-slot:prepend>
        <slot name="prepend"></slot>
      </template>
      <template #default>
        <v-container
          fluid
          class="px-2 px-sm-16 pt-8 overflow-x-hidden"
        >
          <v-row
            dense
            no-gutters
            class="fullscreen-editor-row"
          >
            <v-col
              cols="12"
              md="2"
              class="mb-4 mb-md-0 fullscreen-editor-col"
            >
              <v-card
                class="rounded-r-0 mt-1 default-content--text"
                color="default-panel"
              >
                <v-text-field
                  v-model="dataSearch"
                  append-inner-icon="mdi-magnify"
                  label="Search"
                  @keyup="loadData()"
                  @click:clear="dataSearch = ''; loadData()"
                  single-line
                  hide-details
                  clearable
                  class="px-3"
                ></v-text-field>
                <div
                  style="position: relative;"
                >
                  <v-card-actions
                    class="float-right pa-0"
                    style="position: absolute; top: 0; right: 0;"
                  >
                    <v-tooltip location="bottom"
                    >
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        icon
                        density="compact"
                        variant="flat"
                        rounded="0"
                        elevation="1"
                        color="blue"
                        class="ma-2 fullscreen-editor-btn"
                        v-bind="activatorProps"
                        @click="newData"
                      >
                        <v-icon size="x-small" color="white">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                      <span>New {{ singular }}</span>
                    </v-tooltip>
                  </v-card-actions>
                </div>
                <base-data-table-remote
                  ref="dataTable"
                  :headers="dataTableHeaders"
                  :get-data="getListing"
                  loading-text="Loading..."
                  :no-data-text="'No ' + plural + ' found.'"
                  :search="dataSearch"
                  :show-first-last-page="true"
                  @click:row="selectData"
                >
                  <template #item.actions="{ item }">
                    <div
                      class="text-no-wrap"
                    >
                      <base-data-table-btn
                        :label="'Remove ' + singular"
                        icon="mdi-delete"
                        icon-color="red"
                        @click.stop="confirmRemoveData((item.raw ?? item).id)"
                      ></base-data-table-btn>
                      <base-data-table-btn
                        :label="'Edit ' + singular"
                        icon="mdi-pencil"
                        icon-color="blue"
                        @click.stop="editData(item.raw ?? item)"
                      ></base-data-table-btn>
                    </div>
                  </template>
                </base-data-table-remote>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="10"
              class="fullscreen-editor-col"
            >
              <v-card
                v-if="editorValue"
                class="rounded-l-0"
                color="background"
              >
                <v-card-actions
                  class="float-right"
                  style="position: absolute; top: 0; right: 0;"
                >
                  <v-tooltip location="bottom"
                  >
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        icon
                        density="compact"
                        variant="flat"
                        rounded="0"
                        elevation="1"
                        color="background"
                        class="ma-2 fullscreen-editor-btn"
                        v-bind="activatorProps"
                        @click="resetData"
                      >
                        <v-icon size="x-small" color="grey">mdi-cancel</v-icon>
                      </v-btn>
                    </template>
                    <span>Cancel</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="editorValue.id"
                    location="bottom"
                  >
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        icon
                        density="compact"
                        variant="flat"
                        rounded="0"
                        elevation="1"
                        color="background"
                        class="ma-2 fullscreen-editor-btn"
                        v-bind="activatorProps"
                        @click="confirmRemoveData(editorValue.id)"
                      >
                        <v-icon size="x-small" color="red">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove {{ singular }}</span>
                  </v-tooltip>
                  <v-tooltip location="bottom">
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        icon
                        density="compact"
                        variant="flat"
                        rounded="0"
                        elevation="1"
                        color="background"
                        class="ma-2 fullscreen-editor-btn"
                        v-bind="activatorProps"
                        @click="saveData()"
                        :disabled="(!allowCreate && (editorValue && !editorValue.id))"
                      >
                        <v-icon size="x-small" color="blue">mdi-content-save</v-icon>
                      </v-btn>
                    </template>
                    <span>Save {{ singular }}</span>
                  </v-tooltip>
                  <v-tooltip location="bottom">
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        icon
                        density="compact"
                        variant="flat"
                        rounded="0"
                        elevation="1"
                        color="background"
                        class="ma-2 fullscreen-editor-btn"
                        v-bind="activatorProps"
                        @click="selectData()"
                      >
                        <v-icon size="x-small" color="green">{{ selectIcon }}</v-icon>
                      </v-btn>
                    </template>
                    <span>Select {{ singular }}</span>
                  </v-tooltip>
                  <slot name="actions"></slot>
                </v-card-actions>
                <v-card-title
                  class="headline"
                  style="white-space: pre-wrap;"
                >Edit {{ singular }}: <strong>{{ (editorValue.name) ? (editorValue.name) : ('New ' + singular) }}</strong></v-card-title>
                <v-divider></v-divider>
                <slot></slot>
              </v-card>
              <v-card
                v-else
                class="rounded-l-0"
                color="background"
              >
                <v-card-title
                  class="headline"
                >Select a {{ singular }} or add a new one</v-card-title>
                <v-divider></v-divider>
                <v-row>
                  <v-col
                    cols="12"
                  >
                    <div
                      class="mx-5 my-10 grey--text"
                    >
                      No {{ plural }} selected.
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </base-dialog-fullscreen>
    <base-dialog-delete-confirmation
      v-model="removeId"
      :prompt="'Do you really want to remove this ' + singular + '?'"
      @removed="deleteData()"
    ></base-dialog-delete-confirmation>
  </div>
</template>

<script>
export default {
  name: 'BaseDialogFullscreenEditor',

  inheritAttrs: false,

  props: {
    modelValue: {},
    value: {},
    show: {},
    title: {
      type: String,
      default: "",
    },
    singular: {
      type: String,
      default: "item",
    },
    plural: {
      type: String,
      default: "data",
    },
    selectIcon: {
      type: String,
      default: "mdi-check",
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    dataTableHeaders: {
      type: Array,
      default: null,
    },
    get: {
      type: Function,
      default: null,
    },
    new: {
      type: Function,
      default: null,
    },
    save: {
      type: Function,
      default: null,
    },
    remove: {
      type: Function,
      default: null,
    },
    select: {
      type: Function,
      default: null,
    },
    reset: {
      type: Function,
      default: null,
    },
  },

  emits: ['update:modelValue', 'input', 'show'],

  data: () => ({
    dataSearch: '',
    removeId: null,
  }),

  computed: {
    editorValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    setValue(nextValue) {
      this.$emit('update:modelValue', nextValue);
      this.$emit('input', nextValue);
    },

    onFullscreenInput(isOpen) {
      if (!isOpen) {
        this.$emit('show', null);
      }
    },

    getListing: function (page, itemsPerPage, sortBy, sortDesc) {
      return this.get(page, itemsPerPage, sortBy, sortDesc, this.dataSearch);
    },

    loadData: function () {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.loadData();
      }
    },

    newData: function () {
      // Prop is named `new` (reserved word) — access via bracket notation.
      const create = this['new'];
      if (typeof create === 'function') {
        create();
      }
    },

    saveData: function () {
      if (typeof this.save !== 'function') return;
      Promise.resolve(this.save(this.editorValue))
        .then(() => {
          this.loadData();
        });
    },

    deleteData: function () {
      if (typeof this.remove !== 'function') return;
      Promise.resolve(this.remove(this.removeId))
        .then(() => {
          if (this.editorValue && (this.editorValue.id == this.removeId)) {
            this.setValue(null);
          }
          this.removeId = null;
          this.loadData();
        });
    },

    confirmRemoveData: function (itemId) {
      this.removeId = itemId;
    },

    editData: function (item) {
      const row = item?.raw ?? item;
      if (!row) return;
      this.setValue(row);
    },

    selectData: function (first, second) {
      // Vuetify 3 @click:row → (event, { item }); Select button → no args.
      const raw = second?.item ?? ((first && first.id !== undefined) ? first : null);
      const item = raw?.raw ?? raw;

      if (item) {
        this.setValue(item);
        if (typeof this.select === 'function') {
          this.select(item);
          this.$emit('show', null);
        }
        // No select handler (e.g. Knowledge Base): keep dialog open for editing.
        return;
      }

      if (this.editorValue && typeof this.select === 'function') {
        this.select(this.editorValue);
      }
      this.$emit('show', null);
    },

    resetData: function () {
      this.setValue(null);
      if (typeof this.reset === 'function') {
        this.reset();
      }
    },

  },
};
</script>

<style scoped>
.fullscreen-editor-row {
  max-width: 100%;
}

.fullscreen-editor-col {
  min-width: 0;
}

.fullscreen-editor-btn.v-btn--icon.v-btn--density-compact {
  width: 28px;
  height: 28px;
}

/* Vuetify 3: hide items-per-page selector; keep page navigation. */
:deep(.v-data-table-footer__items-per-page) {
  display: none;
}
</style>
